/**
 * AdminPageView - Pure UI component for admin dashboard
 *
 * This is the View layer that handles only presentation.
 * Contains no logic, hooks, or side effects.
 */
import { Outlet, useLocation } from 'react-router-dom';
import AdminNav from './components/AdminNav';
import type { SystemHealth, AdminMetrics } from './admin.schema';
import './AdminPage.css';

interface AdminPageViewProps {
  systemHealth: SystemHealth | null;
  metrics: AdminMetrics | null;
  loading: boolean;
  error: string | null;
  apiKeyMissing?: boolean;
}

function AdminPageView({ systemHealth, metrics, loading, error, apiKeyMissing }: AdminPageViewProps) {
  const location = useLocation();
  const isDashboard = location.pathname === '/admin';

  if (loading) {
    return (
      <main className="admin-page-container" role="status" aria-live="polite">
        <div className="admin-page-loading">
          <div className="admin-page-loading-text">
            Loading admin dashboard...
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="admin-page-container" role="alert" aria-live="polite">
        <div className="admin-page-error">
          <div className="admin-page-error-text">
            Error loading admin data
          </div>
          <div>{error}</div>
        </div>
      </main>
    );
  }

  return (
    <main className="admin-page-container">
      <AdminNav />
      <div className="admin-page-content">
        {apiKeyMissing && (
          <div className="admin-page-api-key-missing">
            Analytics routes require an API key. Set VITE_QreditCharge_API_KEY or localStorage.QreditCharge_API_KEY and refresh.
          </div>
        )}
        {isDashboard && (
          <>
            <h1 className="admin-page-title">Admin Dashboard</h1>
            
            {systemHealth && (
              <div className="admin-page-card">
                <h2 className="admin-heading">
                  System Health
                </h2>
                <div className="admin-page-grid">
                  <div className="admin-page-metric">
                    <div id="overall-status-value" className={`admin-metric-value-base ${systemHealth.status === 'healthy' ? 'admin-metric-value-green' : 'admin-metric-value-red'}`} aria-labelledby="overall-status-label">
                      {systemHealth.status.toUpperCase()}
                    </div>
                    <div id="overall-status-label" className="admin-page-metric-label">Overall Status</div>
                  </div>
                  
                  <div className="admin-page-metric">
                    <div id="uptime-value" className="admin-metric-value-base admin-metric-value-blue" aria-labelledby="uptime-label">
                      {Math.floor(systemHealth.uptime / 60)}m
                    </div>
                    <div id="uptime-label" className="admin-page-metric-label">Uptime</div>
                  </div>
                  
                  <div className="admin-page-metric">
                    <div id="memory-usage-value" className="admin-metric-value-base admin-metric-value-purple" aria-labelledby="memory-usage-label">
                      {Math.round((systemHealth.memory.used / systemHealth.memory.total) * 100)}%
                    </div>
                    <div id="memory-usage-label" className="admin-page-metric-label">Memory Usage</div>
                  </div>
                  
                  <div className="admin-page-metric">
                    <div id="database-status-value" className={`admin-metric-value-base ${systemHealth.database.connected ? 'admin-metric-value-green' : 'admin-metric-value-red'}`} aria-labelledby="database-status-label">
                      {systemHealth.database.connected ? 'CONNECTED' : 'OFFLINE'}
                    </div>
                    <div id="database-status-label" className="admin-page-metric-label">Database</div>
                  </div>
                </div>
              </div>
            )}

            {metrics && (
              <div className="admin-page-card">
                <h2 className="admin-heading">
                  Business Metrics
                </h2>
                <div className="admin-page-grid">
                  <div className="admin-page-metric">
                    <div id="total-transactions-value" className="admin-metric-value-base admin-metric-value-green" aria-labelledby="total-transactions-label">
                      {metrics.totalTransactions.toLocaleString()}
                    </div>
                    <div id="total-transactions-label" className="admin-page-metric-label">Total Transactions</div>
                  </div>
                  
                  <div className="admin-page-metric">
                    <div id="total-users-value" className="admin-metric-value-base admin-metric-value-blue" aria-labelledby="total-users-label">
                      {metrics.totalUsers.toLocaleString()}
                    </div>
                    <div id="total-users-label" className="admin-page-metric-label">Total Users</div>
                  </div>
                  
                  <div className="admin-page-metric">
                    <div id="revenue-value" className="admin-metric-value-base admin-metric-value-orange" aria-labelledby="revenue-label">
                      ${metrics.revenue.toLocaleString()}
                    </div>
                    <div id="revenue-label" className="admin-page-metric-label">Revenue</div>
                  </div>
                  
                  <div className="admin-page-metric">
                    <div id="active-subscriptions-value" className="admin-metric-value-base admin-metric-value-purple" aria-labelledby="active-subscriptions-label">
                      {metrics.activeSubscriptions.toLocaleString()}
                    </div>
                    <div id="active-subscriptions-label" className="admin-page-metric-label">Active Subscriptions</div>
                  </div>
                </div>
              </div>
            )}

            <div className="admin-page-card">
              <h2 className="admin-heading">
                Quick Actions
              </h2>
              <div className="admin-page-quick-actions-grid admin-page-grid">
                <button type="button" className="admin-page-button">
                  Refresh Data
                </button>
                <button type="button" className="admin-page-button">
                  Export Logs
                </button>
                <button type="button" className="admin-page-button">
                  System Backup
                </button>
                <button type="button" className="admin-page-button">
                  Clear Cache
                </button>
              </div>
            </div>
          </>
        )}
        <Outlet />
      </div>
    </main>
  );
}

export default AdminPageView;
